/**
 * /login — digital-sizer two-tab auth (email-code OTP + password), replacing
 * the Stytch B2B magic-link screen.
 *
 * Email-code: email → POST /auth/send-code → code → POST /auth/verify-code →
 * JWT. Password: email + password → /auth/password-login → JWT. The JWT is
 * handed to AuthProvider.login() which persists it and resolves /auth/me.
 *
 * Styling follows the Eastview "Sign in" design: Plus Jakarta Sans, a brand
 * lockup, a segmented Email-code / Password control, icon-leading filled
 * fields, and a gradient submit button.
 */
import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";

import { authApi } from "../api/authApi";
import { useAuth } from "../auth/AuthProvider";
import LendmarqLogo from "../assets/LendmarqLogo";
import {
  hasSignedInBefore,
  partnerStorage,
} from "../utils/storage/partnerStorage";

// Remember the last sign-in method the user picked (partner-scoped).
const TAB_STORAGE_KEY = "login.preferredTab";
const readPreferredTab = () =>
  partnerStorage.getItem(TAB_STORAGE_KEY) === "password" ? "password" : "code";

// Remember the "Remember me" preference itself (partner-scoped). Default ON,
// which matches the historical behavior of always persisting to localStorage.
const REMEMBER_STORAGE_KEY = "login.rememberMe";
const readRememberMe = () =>
  partnerStorage.getItem(REMEMBER_STORAGE_KEY) !== "false";
const writeRememberMe = (val) =>
  partnerStorage.setItem(REMEMBER_STORAGE_KEY, val ? "true" : "false");

// Remember the last-used email (partner-scoped) so it can be prepopulated when
// "Remember me on this device" is on. Saved on a successful login while remember
// is checked; cleared when remember is turned off.
const SAVED_EMAIL_KEY = "login.savedEmail";
const readSavedEmail = () => partnerStorage.getItem(SAVED_EMAIL_KEY) || "";
const writeSavedEmail = (email) =>
  partnerStorage.setItem(SAVED_EMAIL_KEY, email || "");
const clearSavedEmail = () => partnerStorage.removeItem(SAVED_EMAIL_KEY);

// Persist the saved email to match the remember preference: keep it when
// remember is on, drop it otherwise.
const syncSavedEmail = (email, remember) =>
  remember ? writeSavedEmail(email) : clearSavedEmail();

// Initial email for the login inputs: a pending-code email wins; otherwise the
// saved email when "Remember me" is on.
const initialEmail = (pendingEmail) =>
  pendingEmail || (readRememberMe() ? readSavedEmail() : "");

// Remember that an email code was sent so the user can leave the page (to grab
// the code from their inbox) and come back to the code-entry step — instead of
// landing on a blank email field and having to request a new code. Persisted
// partner-scoped, with a TTL so a stale (likely-expired) code isn't restored.
const PENDING_CODE_KEY = "login.pendingCode";
const PENDING_CODE_TTL_MS = 15 * 60 * 1000; // 15 minutes

const readPendingCode = () => {
  try {
    const raw = partnerStorage.getItem(PENDING_CODE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !parsed?.sentAt) return null;
    if (Date.now() - parsed.sentAt > PENDING_CODE_TTL_MS) {
      partnerStorage.removeItem(PENDING_CODE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writePendingCode = (email) =>
  partnerStorage.setItem(
    PENDING_CODE_KEY,
    JSON.stringify({ email, sentAt: Date.now() }),
  );

const clearPendingCode = () => partnerStorage.removeItem(PENDING_CODE_KEY);

// --- design tokens (mirrors the mockup :root vars) ----------------------------
// Brand colors match the app header (TopHeader.js). Lendmarq blue (#1F5AF2).
const T = {
  brand: "#1F5AF2", // Lendmarq brand blue
  brandDark: "#1648CC", // darker blue — solid / gradient-bottom / hover
  ink: "#0f2330",
  muted: "#6b7c87",
  line: "#e4e9ed",
  field: "#f7f9fb",
  white: "#ffffff",
  font: "'Plus Jakarta Sans', sans-serif",
};

// --- inline icons -------------------------------------------------------------
const MailIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const LockIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const KeyholeIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="10" r="3" />
    <path d="M11 13h2l-1 7-1-7Z" />
  </svg>
);

const EyeIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
    <path d="m2 2 20 20" />
  </svg>
);

// --- reusable field with a leading icon ---------------------------------------
// When `type="password"`, an eye button is rendered on the right to toggle
// visibility (the field stays type=password by default for password-manager
// compatibility).
const IconField = ({ icon: Icon, label, id, type, ...inputProps }) => {
  const isPassword = type === "password";
  const [reveal, setReveal] = useState(false);
  const inputType = isPassword ? (reveal ? "text" : "password") : type;

  return (
    <Box sx={{ mb: "18px" }}>
      <Box
        component="label"
        htmlFor={id}
        sx={{
          display: "block",
          fontSize: "12.5px",
          fontWeight: 600,
          color: T.ink,
          mb: "7px",
        }}
      >
        {label}
      </Box>
      <Box sx={{ position: "relative" }}>
        <Box
          component={Icon}
          sx={{
            position: "absolute",
            left: "14px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "17px",
            height: "17px",
            stroke: T.muted,
            pointerEvents: "none",
          }}
        />
        <Box
          component="input"
          id={id}
          type={inputType}
          {...inputProps}
          sx={{
            width: "100%",
            fontFamily: T.font,
            fontSize: "14px",
            color: T.ink,
            background: T.field,
            border: `1px solid ${T.line}`,
            borderRadius: "11px",
            p: isPassword ? "13px 44px 13px 42px" : "13px 14px 13px 42px",
            transition: "border-color .2s, box-shadow .2s, background .2s",
            "&::placeholder": { color: "#9aabb4" },
            "&:focus": {
              outline: "none",
              background: T.white,
              borderColor: T.brand,
              boxShadow: `0 0 0 4px rgba(31,90,242,.14)`,
            },
            "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
          }}
        />
        {isPassword && (
          <Box
            component="button"
            type="button"
            tabIndex={-1}
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? "Hide password" : "Show password"}
            title={reveal ? "Hide password" : "Show password"}
            sx={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "grid",
              placeItems: "center",
              width: "30px",
              height: "30px",
              p: 0,
              border: 0,
              borderRadius: "8px",
              background: "transparent",
              cursor: "pointer",
              "&:hover": { background: "rgba(15,35,48,.06)" },
            }}
          >
            <Box
              component={reveal ? EyeOffIcon : EyeIcon}
              sx={{ width: "17px", height: "17px", stroke: T.muted }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// --- gradient submit button ---------------------------------------------------
const GradientButton = ({ children, ...props }) => (
  <Box
    component="button"
    {...props}
    sx={{
      width: "100%",
      fontFamily: T.font,
      fontSize: "14.5px",
      fontWeight: 700,
      color: "#fff",
      background: `linear-gradient(180deg, #3D72FF, ${T.brandDark})`,
      border: 0,
      borderRadius: "11px",
      p: "14px",
      cursor: "pointer",
      mt: "4px",
      boxShadow: "0 8px 20px rgba(31,90,242,.28)",
      transition: "transform .12s ease, box-shadow .2s ease, filter .2s",
      "&:hover": {
        filter: "brightness(1.07)",
        boxShadow: "0 10px 26px rgba(31,90,242,.34)",
      },
      "&:active": { transform: "translateY(1px)" },
      "&:disabled": { opacity: 0.65, cursor: "not-allowed", filter: "none" },
    }}
  >
    {children}
  </Box>
);

// --- secondary (text) button --------------------------------------------------
const TextButton = ({ children, sx, ...props }) => (
  <Box
    component="button"
    type="button"
    {...props}
    sx={{
      background: "transparent",
      border: 0,
      cursor: "pointer",
      fontFamily: T.font,
      fontSize: "13px",
      fontWeight: 600,
      color: T.brand,
      p: 0,
      "&:hover": { textDecoration: "underline" },
      "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// --- "Remember me on this device" checkbox (shared by both login forms) -------
const RememberMeField = ({ checked, onChange }) => (
  <Box
    component="label"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "9px",
      mb: "18px",
      cursor: "pointer",
      userSelect: "none",
      fontSize: "13px",
      fontWeight: 500,
      color: T.muted,
    }}
  >
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      sx={{
        width: "16px",
        height: "16px",
        accentColor: T.brand,
        cursor: "pointer",
        m: 0,
      }}
    />
    Remember me on this device
  </Box>
);

// Only allow same-origin path redirects (no protocol-relative // or /\).
const sanitizeNext = (raw) => {
  if (!raw || !raw.startsWith("/")) return "/loans";
  if (raw.startsWith("//") || raw.startsWith("/\\")) return "/loans";
  return raw;
};

const LoginScreen = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(readPreferredTab);

  const selectTab = (value) => {
    setTab(value);
    partnerStorage.setItem(TAB_STORAGE_KEY, value);
  };

  const [mode, setMode] = useState("login"); // login | forgot | setup
  // Hide the first-time "set up a password" prompt once the user has ever
  // signed in on this device.
  const [showSetupPrompt] = useState(() => !hasSignedInBefore());

  const params = new URLSearchParams(location.search);
  const next = sanitizeNext(params.get("next") ?? params.get("redirect"));

  if (user) {
    return <Navigate to={next} replace />;
  }

  const onLogin = async (token, options) => {
    await login(token, options);
    navigate(next, { replace: true });
  };

  return (
    <Box
      sx={{
        fontFamily: T.font,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "24px",
        color: T.ink,
        WebkitFontSmoothing: "antialiased",
        background: `
          radial-gradient(1200px 600px at 85% -10%, rgba(31,90,242,.10), transparent 60%),
          radial-gradient(900px 500px at -10% 110%, rgba(31,90,242,.08), transparent 55%),
          #eef2f5`,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "408px",
          background: T.white,
          border: `1px solid ${T.line}`,
          borderRadius: "22px",
          boxShadow:
            "0 1px 2px rgba(15,35,48,.04), 0 12px 40px rgba(15,35,48,.08)",
          p: "40px 38px 34px",
          animation: "loginRise .5s cubic-bezier(.2,.8,.25,1) both",
          "@keyframes loginRise": {
            from: { opacity: 0, transform: "translateY(14px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* brand lockup — partner logo (Lendmarq) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: "30px",
          }}
        >
          <LendmarqLogo height={38} />
        </Box>

        {mode !== "login" ? (
          <ForgotPasswordForm
            variant={mode}
            onBack={() => setMode("login")}
          />
        ) : (
          <>
            {/* heading */}
            <Box sx={{ mb: "22px" }}>
              <Box sx={{ fontSize: "21px", fontWeight: 700 }}>Sign in</Box>
              <Box sx={{ fontSize: "13.5px", color: T.muted, mt: "4px" }}>
                Access your loan portfolio dashboard.
              </Box>
            </Box>

            {/* segmented tabs */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                background: T.field,
                border: `1px solid ${T.line}`,
                borderRadius: "11px",
                p: "4px",
                mb: "24px",
              }}
            >
              {[
                { value: "code", label: "Email code" },
                { value: "password", label: "Password" },
              ].map((t) => (
                <Box
                  key={t.value}
                  component="button"
                  type="button"
                  onClick={() => selectTab(t.value)}
                  sx={{
                    border: 0,
                    cursor: "pointer",
                    fontFamily: T.font,
                    fontSize: "13.5px",
                    fontWeight: 600,
                    color: tab === t.value ? T.ink : T.muted,
                    p: "9px 0",
                    borderRadius: "8px",
                    background: tab === t.value ? T.white : "transparent",
                    boxShadow:
                      tab === t.value
                        ? "0 1px 3px rgba(15,35,48,.10)"
                        : "none",
                    transition: "all .2s ease",
                  }}
                >
                  {t.label}
                </Box>
              ))}
            </Box>

            {tab === "code" ? (
              <EmailCodeForm onLogin={onLogin} />
            ) : (
              <PasswordForm onLogin={onLogin} />
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                mt: "22px",
              }}
            >
              <TextButton onClick={() => setMode("forgot")}>
                Forgot password?
              </TextButton>
              {showSetupPrompt && (
                <TextButton onClick={() => setMode("setup")}>
                  First time here? Set up a password
                </TextButton>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const EmailCodeForm = ({ onLogin }) => {
  // Restore a pending code request so returning to the page (after grabbing the
  // code from email) drops the user straight onto the code-entry step.
  const pending = readPendingCode();
  const [email, setEmail] = useState(() => initialEmail(pending?.email));
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState(pending ? "code" : "email");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(readRememberMe);

  const toggleRemember = (val) => {
    setRemember(val);
    writeRememberMe(val);
    syncSavedEmail(email, val);
  };

  const resetToEmail = () => {
    clearPendingCode();
    setCode("");
    setError(null);
    setPhase("email");
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.sendCode(email);
      writePendingCode(email);
      setPhase("code");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to send code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await authApi.verifyCode(email, code);
      clearPendingCode();
      syncSavedEmail(email, remember);
      await onLogin(res.access_token, { remember });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={phase === "email" ? sendCode : verifyCode}>
      <IconField
        icon={MailIcon}
        id="email"
        label="Email address"
        type="email"
        autoFocus
        autoComplete="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={phase === "code"}
        required
      />
      {phase === "code" && (
        <IconField
          icon={KeyholeIcon}
          id="code"
          label="6-digit code"
          autoFocus
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="• • • • • •"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          required
        />
      )}
      {error && (
        <Alert severity="error" sx={{ mb: "18px" }}>
          {error}
        </Alert>
      )}
      <RememberMeField checked={remember} onChange={toggleRemember} />
      <GradientButton type="submit" disabled={busy}>
        {phase === "email" ? "Send code" : "Verify"}
      </GradientButton>
      {phase === "code" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            mt: "14px",
          }}
        >
          <TextButton onClick={sendCode} disabled={busy}>
            Resend code
          </TextButton>
          <Box sx={{ width: "1px", height: "13px", background: T.line }} />
          <TextButton onClick={resetToEmail} disabled={busy}>
            Use a different email
          </TextButton>
        </Box>
      )}
    </Box>
  );
};

const PasswordForm = ({ onLogin }) => {
  const [email, setEmail] = useState(() => initialEmail());
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(readRememberMe);

  const toggleRemember = (val) => {
    setRemember(val);
    writeRememberMe(val);
    syncSavedEmail(email, val);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await authApi.passwordLogin(email, password);
      syncSavedEmail(email, remember);
      await onLogin(res.access_token, { remember });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Invalid email or password",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit}>
      <IconField
        icon={MailIcon}
        id="email"
        label="Email address"
        type="email"
        autoComplete="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <IconField
        icon={LockIcon}
        id="password"
        label="Password"
        type="password"
        autoComplete="current-password"
        placeholder="Enter your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      {error && (
        <Alert severity="error" sx={{ mb: "18px" }}>
          {error}
        </Alert>
      )}
      <RememberMeField checked={remember} onChange={toggleRemember} />
      <GradientButton type="submit" disabled={busy}>
        Sign in
      </GradientButton>
    </Box>
  );
};

// Copy differs for the "forgot" (reset an existing password) vs "setup" (first
// time — create a password) entry points. Both hit the same timing-safe
// requestPasswordReset endpoint and land on /reset-password via the emailed link.
const FORGOT_COPY = {
  forgot: {
    title: "Reset your password",
    subtitle: "Enter your email and we'll send you a reset link.",
    button: "Send reset link",
    sentBody: (email) =>
      `If an account exists for ${email}, we've emailed a password reset link. It expires in 30 minutes.`,
    failMsg: "Failed to send reset link",
  },
  setup: {
    title: "Set up your password",
    subtitle: "Enter your email and we'll send you a link to create a password.",
    button: "Send setup link",
    sentBody: (email) =>
      `If an account exists for ${email}, we've emailed a link to set up your password. It expires in 30 minutes.`,
    failMsg: "Failed to send setup link",
  },
};

const ForgotPasswordForm = ({ onBack, variant = "forgot" }) => {
  const copy = FORGOT_COPY[variant] || FORGOT_COPY.forgot;
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.requestPasswordReset(email);
      // Always treat as success — the request is timing-safe and never reveals
      // whether the account exists.
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || copy.failMsg);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Box>
        <Box sx={{ fontSize: "18px", fontWeight: 700, mb: "8px" }}>
          Check your email
        </Box>
        <Alert severity="success" sx={{ mb: "18px" }}>
          {copy.sentBody(email)}
        </Alert>
        <GradientButton type="button" onClick={onBack}>
          Back to sign in
        </GradientButton>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Box sx={{ mb: "22px" }}>
        <Box sx={{ fontSize: "21px", fontWeight: 700 }}>{copy.title}</Box>
        <Box sx={{ fontSize: "13.5px", color: T.muted, mt: "4px" }}>
          {copy.subtitle}
        </Box>
      </Box>
      <IconField
        icon={MailIcon}
        id="email"
        label="Email address"
        type="email"
        autoFocus
        autoComplete="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      {error && (
        <Alert severity="error" sx={{ mb: "18px" }}>
          {error}
        </Alert>
      )}
      <GradientButton type="submit" disabled={busy}>
        {busy ? "Sending…" : copy.button}
      </GradientButton>
      <Box sx={{ textAlign: "center", mt: "14px" }}>
        <TextButton onClick={onBack}>Back to sign in</TextButton>
      </Box>
    </Box>
  );
};

export default LoginScreen;
